/**
 * Swept analytic collision (FS-05 §2.3, §4.4). The ship does not move in Z; the hazard does, so the
 * "sweep" is the hazard's Z travel this sub-step. The crossing time is *solved*, not sampled, so
 * there is no Z window to tunnel through at any speed. No physics engine, no curve walking, and — per
 * FS-05 R25 — no try/catch that turns a throw into "no collision".
 */
import { angularDelta } from '../core/math-det';
import type { ResolvedModifiers } from './modifiers';
import type { ShipState } from './flight-model';

export const enum CollisionOutcome {
  None = 0,
  Graze = 1,
  NodeCentre = 2,
  Hit = 3,
}

export interface HazardCollider {
  /** Signed distance in the hazard's plane; negative inside the lethal region, |∇d|≈1 near surface. */
  signedDistance(x: number, y: number, tLocal: number): number;
  /** Lethal Z extent in *world* space (hazard origin already added). */
  readonly zNear: number;
  readonly zFar: number;
  readonly nodeTheta: number;
  readonly nodeHalfTheta: number;
  readonly tLocal: number;
  lethal: boolean;
  grazed: boolean;
  nodeAwarded: boolean;
  resolved: boolean;
}

export interface CollisionResult {
  outcome: CollisionOutcome;
  nodeCentre: boolean;
  u: number;
  distance: number;
  nx: number;
  ny: number;
}

const SWEEP_SUBDIVIDE_U = 0.35;
const CONTINUOUS_SAMPLES = 5;
const GRAD_EPS = 0.01;
const NODE_CENTRE_FRACTION = 0.25;
const NODE_MIN_R = 0.5;

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** One sub-step test. Writes into `out`. Allocation-free. `hazardDz` = scrollSpeed * dt (> 0). */
export function testCollision(
  s: ShipState,
  h: HazardCollider,
  hazardDz: number,
  mods: ResolvedModifiers,
  out: CollisionResult,
): void {
  out.outcome = CollisionOutcome.None;
  out.nodeCentre = false;
  out.distance = Infinity;
  out.u = 0;
  out.nx = 0;
  out.ny = 0;

  // Broadphase: did the lethal Z extent overlap z = 0 at any point this sub-step?
  // Swept extent over the sub-step is [zNear - dz, zFar].
  if (h.zNear - hazardDz > 0 || h.zFar < 0) return;

  // Crossing fraction of the hazard centre passing z = 0.
  const zc = 0.5 * (h.zNear + h.zFar);
  let u = hazardDz > 1e-6 ? 1 - zc / hazardDz : 1;
  if (u < 0) u = 0;
  else if (u > 1) u = 1;

  const xu = lerp(s.prevX, s.x, u);
  const yu = lerp(s.prevY, s.y, u);

  // Evaluate the SDF at the crossing.
  let d = h.signedDistance(xu, yu, h.tLocal);

  // Thick hazards: the ship travels *along* the hazard — sample across the overlap.
  if (h.zFar - h.zNear > SWEEP_SUBDIVIDE_U * hazardDz) {
    for (let i = 0; i < CONTINUOUS_SAMPLES; i++) {
      const ui = i / (CONTINUOUS_SAMPLES - 1);
      const xi = lerp(s.prevX, s.x, ui);
      const yi = lerp(s.prevY, s.y, ui);
      const di = h.signedDistance(xi, yi, h.tLocal);
      if (di < d) {
        d = di;
        u = ui;
      }
    }
  }

  out.u = u;
  out.distance = d;

  // Node-centre award (independent of hit/graze), latched by caller.
  const r = Math.hypot(xu, yu);
  if (r >= NODE_MIN_R && h.nodeHalfTheta > 0) {
    const theta = wrapTheta(xu, yu);
    if (Math.abs(angularDelta(theta, h.nodeTheta)) < NODE_CENTRE_FRACTION * h.nodeHalfTheta) {
      out.nodeCentre = true;
    }
  }

  if (mods.intangible || !h.lethal) {
    out.outcome = CollisionOutcome.None;
    return;
  }

  if (d < mods.hitboxRadius) {
    out.outcome = CollisionOutcome.Hit;
  } else if (d < mods.grazeRadius) {
    out.outcome = CollisionOutcome.Graze;
  } else {
    out.outcome = CollisionOutcome.None;
    return;
  }

  // Surface→ship unit normal from the SDF gradient (central difference).
  const dgx = h.signedDistance(xu + GRAD_EPS, yu, h.tLocal) - h.signedDistance(xu - GRAD_EPS, yu, h.tLocal);
  const dgy = h.signedDistance(xu, yu + GRAD_EPS, h.tLocal) - h.signedDistance(xu, yu - GRAD_EPS, h.tLocal);
  const gl = Math.hypot(dgx, dgy) || 1;
  out.nx = dgx / gl;
  out.ny = dgy / gl;
}

function wrapTheta(x: number, y: number): number {
  let a = Math.atan2(x, y);
  if (a < 0) a += Math.PI * 2;
  return a;
}
